// Tiny DOM builders (§15.1: textContent only — innerHTML is lint-banned). Shared by the sign-in/teams/
// account flows and the lesson overlays. No framework — the account UI is light enough for hand-built DOM.
//
// D239 (the user, looking at them): these wear the KOLONY HUD's language now — cream panels on a
// teal scrim, wood borders, gold primaries with the hard offset shadow, Fredoka throughout. D157
// had retokened them to the FAFI-derived ramp (dark green panels, amber accent, the UI face) and
// said so in as many words: "Coherence only: the full redesigns of these flows are their own
// handoffs." This is that. The values are taken from `.modal`/`.modal-scrim` in theme.css rather
// than invented, so a dialogue opened from the menu and one opened over the board are the same
// object — that is the whole point, and it is why they are not new numbers.

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, style = '', text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (style) node.style.cssText = style;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function div(style = '', text?: string): HTMLDivElement {
  return el('div', style, text);
}

export function button(label: string, onClick: () => void, opts: { primary?: boolean; active?: boolean } = {}): HTMLButtonElement {
  const b = el('button', '', label);
  // A gold primary that sits on a hard wood offset, exactly like the menu's own; anything else
  // is the quiet white card the in-game modals use for their rows.
  // The gold gradient is written with LITERALS, not `var(--hud-gold)`: jsdom drops a whole
  // declaration when a custom property appears inside a gradient, which would make the one
  // style that says "this is the primary" invisible to every test. The literals are pinned
  // against the token in dom.test.ts so they cannot drift apart.
  const bg = opts.primary
    ? 'linear-gradient(180deg,#f3c568,#e8b64c)'
    : opts.active
      ? 'var(--hud-border-pale)'
      : 'var(--hud-card)';
  const border = opts.primary ? 'var(--hud-wood-border)' : 'var(--hud-border)';
  const shadow = opts.primary ? '0 4px 0 var(--hud-wood-border)' : 'none';
  b.style.cssText =
    `margin:3px;padding:${opts.primary ? '11px 22px' : '9px 16px'};font-size:${opts.primary ? '16px' : '14px'};` +
    `font-weight:700;border:${opts.primary ? '2.5px' : '2px'} solid ${border};background:${bg};color:var(--hud-ink);` +
    `border-radius:${opts.primary ? '14px' : '12px'};cursor:pointer;font-family:var(--font-hud);box-shadow:${shadow}`;
  b.onclick = onClick;
  return b;
}

export function textInput(placeholder: string, value = '', type = 'text'): HTMLInputElement {
  const i = el('input');
  i.type = type;
  i.placeholder = placeholder;
  i.value = value;
  i.style.cssText =
    'margin:6px 0;padding:11px 13px;width:100%;box-sizing:border-box;border:2px solid var(--hud-border);' +
    'background:var(--hud-card);color:var(--hud-ink);border-radius:12px;font-family:var(--font-hud);font-size:15px';
  return i;
}

export function note(text: string, color = 'var(--hud-ink-muted)'): HTMLDivElement {
  return div(`margin:8px 0;font-size:13.5px;color:${color}`, text);
}

/** A centred full-screen overlay panel container (returns {overlay, mount}). Modal scrim level. */
/**
 * A full-screen overlay with a centred panel, ATTACHED to the document.
 *
 * D218: it used to return the overlay detached and leave attaching to the caller, and of
 * the three callers only one remembered — so the Friends hub and the new ladder both
 * painted their contents into an orphan tree, fired their real HTTP requests, and showed
 * the player nothing at all. Nothing failed: no console error, no rejected promise, just a
 * button that did not work. A factory whose contract is "and then you must remember to
 * append it" is a factory with a trap in it; this one attaches its own overlay.
 */
export function overlayPanel(doc: Document = document, opts: { wide?: boolean } = {}): { overlay: HTMLDivElement; mount: (content: HTMLElement) => void } {
  const overlay = div(
    'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(8,38,46,.62);' +
      'backdrop-filter:blur(2px);color:var(--hud-ink);font-family:var(--font-hud);z-index:120;overflow:auto;padding:24px',
  );
  // D248: `wide` is a HOST, not a second panel — the dashboard draws its own cream card, so
  // a second one around it would double every border and cap it at a phone's width.
  const panel = opts.wide
    ? div('max-width:100%;width:auto;display:flex;justify-content:center')
    : div(
        'min-width:340px;max-width:560px;width:100%;padding:22px 24px;border:3px solid var(--hud-border);' +
          'border-radius:20px;background:var(--hud-cream);box-shadow:0 18px 50px rgba(0,0,0,.45)',
      );
  overlay.append(panel);
  doc.body.append(overlay);
  return {
    overlay,
    mount: (content) => panel.replaceChildren(content),
  };
}
